import { type Permission } from "@vega-api/generated/types/permission";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { Switch } from "@vega-core/components/Switch/Switch";
import { PermissionsExpandToggle } from "@vega-modules/components/PermissionsExpandToggle/PermissionsExpandToggle";

import { usePermissionCollapse } from "../hooks/usePermissionCollapse";

type PermissionsListProps = {
  permissions: RoleNew["permissions"];
  currentPermissions: Record<string, Permission> | undefined;
  onPermissionToggle: (permissionId: string, enabled: boolean) => void;
};

export const PermissionsList = ({
  permissions,
  currentPermissions,
  onPermissionToggle,
}: PermissionsListProps) => {
  const {
    visiblePermissions,
    hasMorePermissions,
    remainingCount,
    showAll: isExpanded,
    toggleShowAll,
  } = usePermissionCollapse({ permissions });

  return (
    <div className="flex flex-col gap-4">
      {visiblePermissions.map((item) => {
        const permission = currentPermissions?.[item.id];
        const isEnabled = permission?.enabled ?? false;
        const isCustomizable = item.customizable;

        return (
          <div className="flex justify-between items-center py-3" key={item.id}>
            <span className="body-base-semibold text-text/primary">
              {item.description}
            </span>
            <Switch
              checked={isEnabled}
              className="shrink-0"
              disabled={!isCustomizable}
              id={`permission-${item.id}`}
              onChange={() => onPermissionToggle(item.id, !isEnabled)}
            />
          </div>
        );
      })}

      <PermissionsExpandToggle
        dataTestId="toggle-permissions"
        hasMore={hasMorePermissions}
        isExpanded={isExpanded}
        onToggle={toggleShowAll}
        remainingCount={remainingCount}
      />
    </div>
  );
};
